/**
 * Persistent in-app banner that surfaces the driver's currently-pending
 * offer, in case the push notification was missed. Polls every 5s.
 * Clicking it deep-links to /driver/offer/{id}.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, ChevronRight } from "lucide-react";
import driverApi from "@/lib/driverApi";

const POLL_MS = 5000;

export default function PendingOfferBanner() {
  const [offer, setOffer] = useState(null);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await driverApi.get("/driver/offers/pending");
        if (!alive) return;
        setOffer(data.offer);
        setBooking(data.booking);
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  if (!offer) return null;

  const remaining = Math.max(0,
    Math.floor((new Date(offer.expires_at).getTime() - Date.now()) / 1000));

  return (
    <Link
      to={`/driver/offer/${offer.id}`}
      data-testid="pending-offer-banner"
      className="mb-4 block rounded-2xl border border-emerald-500/60 bg-emerald-500/10 p-4 relative overflow-hidden animate-pulse-slow"
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-emerald-500 text-black flex items-center justify-center shrink-0">
          <Zap className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-medium truncate">
            Nova ponuda · {booking?.pickup_location || "-"}
          </p>
          <p className="text-[11px] text-emerald-200 truncate">
            → {booking?.dropoff_location || "-"}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-display text-2xl text-white tabular-nums">
            {remaining}s
          </p>
          <p className="text-[10px] uppercase tracking-widest text-emerald-200">
            Otvori
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-emerald-200" />
      </div>
    </Link>
  );
}
